import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, AlertTriangle, CalendarX } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { HOLIDAYS } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

/**
 * Public holidays admin. The ×2.0 rule only applies when a holiday is BOTH
 * dated AND confirmed. Movable holidays are seeded name-only so Michael sees
 * them as outstanding rather than as a silent guess.
 */

export default function PublicHolidays() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [rows, setRows] = useState([]);
    const [name, setName] = useState("");
    const [dateStr, setDateStr] = useState("");
    const [drafts, setDrafts] = useState({}); // { id: {name, date, confirmed} }

    const load = async () => {
        try {
            const { data } = await api.get("/payroll/holidays");
            setRows(data);
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail));
        }
    };
    useEffect(() => { load(); }, []);

    const draft = (id) => drafts[id] || {};
    const patchDraft = (id, part) => setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), ...part } }));

    const create = async () => {
        if (!name.trim()) return;
        try {
            await api.post("/payroll/holidays", {
                name: name.trim(),
                date: dateStr || null,
                confirmed: false,
            });
            setName(""); setDateStr("");
            load();
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail));
        }
    };

    const persistPatch = async (row) => {
        const d = draft(row.id);
        if (!Object.keys(d).length) return;
        try {
            await api.patch(`/payroll/holidays/${row.id}`, d);
            setDrafts((s) => { const c = { ...s }; delete c[row.id]; return c; });
            load();
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Remove this public holiday?")) return;
        try {
            await api.delete(`/payroll/holidays/${id}`);
            load();
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail));
        }
    };

    const noDate = rows.filter((r) => !r.date);

    return (
        <div data-testid={HOLIDAYS.page} className="p-10 max-w-[1000px]">
            <div className="mb-8">
                <div className="text-[11px] uppercase tracking-[0.24em] text-neutral-500 font-display">Settings · Public holidays</div>
                <h1 className="font-display font-bold text-4xl mt-1 text-[color:var(--kp-charcoal)]">
                    PNG public holidays
                </h1>
                <p className="text-sm text-neutral-500 mt-2 leading-relaxed max-w-[720px]">
                    The ×2.0 public-holiday rule only applies to entries that are <strong>both dated and confirmed</strong>.
                    Movable holidays (Good Friday, Easter etc.) are seeded with no date on purpose — until the
                    authoritative list arrives from Ben, the app treats those days as ordinary rather than
                    quietly guessing.
                </p>
                {!isAdmin && (
                    <div
                        data-testid="holidays-readonly-chip"
                        className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 text-[11px] font-display font-semibold uppercase tracking-[0.14em] rounded-[3px]"
                        style={{ background: "#F0F0F1", color: "var(--kp-charcoal)", borderLeft: "3px solid #C88A17" }}
                    >
                        <ShieldCheck size={12} style={{ color: "#C88A17" }} />
                        Read-only · an admin can change these
                    </div>
                )}
            </div>

            {noDate.length > 0 && (
                <div className="mb-6 border border-amber-300 bg-amber-50 rounded-[3px] p-4 flex items-start gap-3">
                    <CalendarX size={16} className="text-amber-600 mt-0.5" />
                    <div className="text-[13px] text-amber-900">
                        <strong>{noDate.length} holiday{noDate.length > 1 ? "s" : ""} still without a date:</strong>{" "}
                        {noDate.map((h) => h.name).join(", ")}. These will be treated as ordinary days
                        until you set a date and mark them Confirmed.
                    </div>
                </div>
            )}

            {isAdmin && (
                <div className="mb-8 bg-white border border-neutral-200 rounded-[3px] p-4">
                    <div className="text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-display mb-2">Add a holiday</div>
                    <div className="grid grid-cols-[1fr_180px_auto] gap-3">
                        <input
                            data-testid={HOLIDAYS.newName}
                            placeholder="Name (e.g. Queen's Birthday)"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="kp-focus w-full border border-neutral-300 px-3 py-2 text-sm bg-white rounded-[3px]"
                        />
                        <input
                            data-testid={HOLIDAYS.newDate}
                            type="date"
                            value={dateStr}
                            onChange={(e) => setDateStr(e.target.value)}
                            className="kp-focus w-full border border-neutral-300 px-3 py-2 text-sm bg-white rounded-[3px] font-mono-money"
                        />
                        <button
                            data-testid={HOLIDAYS.newSaveBtn}
                            onClick={create}
                            disabled={!name.trim()}
                            className="inline-flex items-center gap-2 px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px] disabled:opacity-40"
                            style={{ background: "var(--kp-red)" }}
                        >
                            <Plus size={14} /> Add
                        </button>
                    </div>
                </div>
            )}

            <div className="bg-white border border-neutral-200 rounded-[3px] overflow-hidden">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-display"
                            style={{ background: "#FAFAFA" }}>
                            <th className="px-5 py-3 font-semibold">Name</th>
                            <th className="px-5 py-3 font-semibold">Date</th>
                            <th className="px-5 py-3 font-semibold">Status</th>
                            <th className="px-5 py-3 font-semibold text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((h) => {
                            const d = draft(h.id);
                            const effName = d.name ?? h.name;
                            const effDate = d.date ?? h.date ?? "";
                            const effConfirmed = "confirmed" in d ? d.confirmed : h.confirmed;
                            const changed = Object.keys(d).length > 0;
                            const treatedAsPh = effConfirmed && effDate;
                            return (
                                <tr key={h.id} data-testid={HOLIDAYS.row(h.id)} className="border-t border-neutral-100">
                                    <td className="px-5 py-3">
                                        {isAdmin ? (
                                            <input
                                                data-testid={HOLIDAYS.editName(h.id)}
                                                value={effName}
                                                onChange={(e) => patchDraft(h.id, { name: e.target.value })}
                                                className="w-full border-0 bg-transparent focus:outline-none text-[color:var(--kp-charcoal)] font-medium"
                                            />
                                        ) : (
                                            <span className="text-[color:var(--kp-charcoal)] font-medium">{effName}</span>
                                        )}
                                        {h.notes && <div className="text-[11px] text-neutral-400 mt-0.5">{h.notes}</div>}
                                    </td>
                                    <td className="px-5 py-3 w-[180px]">
                                        {isAdmin ? (
                                            <input
                                                data-testid={HOLIDAYS.editDate(h.id)}
                                                type="date"
                                                value={effDate || ""}
                                                onChange={(e) => patchDraft(h.id, { date: e.target.value || null })}
                                                className="kp-focus w-full border border-neutral-200 px-2 py-1 text-[12px] bg-white rounded-[2px] font-mono-money"
                                            />
                                        ) : (
                                            <span className="font-mono-money text-[13px] text-neutral-700">{effDate || "—"}</span>
                                        )}
                                    </td>
                                    <td className="px-5 py-3">
                                        {isAdmin ? (
                                            <label className="inline-flex items-center gap-2 text-[12px] cursor-pointer">
                                                <input
                                                    data-testid={HOLIDAYS.editConfirmed(h.id)}
                                                    type="checkbox"
                                                    checked={!!effConfirmed}
                                                    onChange={(e) => patchDraft(h.id, { confirmed: e.target.checked })}
                                                    className="accent-[color:var(--kp-success)]"
                                                />
                                                {treatedAsPh ? (
                                                    <span className="inline-flex items-center gap-1 text-[color:var(--kp-success)] font-display uppercase tracking-[0.14em] text-[10px] font-semibold">
                                                        <ShieldCheck size={10} /> Live (×2.0)
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-amber-700 font-display uppercase tracking-[0.14em] text-[10px] font-semibold">
                                                        <AlertTriangle size={10} /> {effDate ? "Unconfirmed" : "No date"}
                                                    </span>
                                                )}
                                            </label>
                                        ) : (
                                            <span className="text-[11px] text-neutral-500 font-display uppercase tracking-[0.14em]">
                                                {treatedAsPh ? "Live" : effDate ? "Unconfirmed" : "No date"}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-5 py-3 text-right">
                                        {isAdmin && (
                                            <div className="inline-flex items-center gap-3">
                                                {changed && (
                                                    <button
                                                        onClick={() => persistPatch(h)}
                                                        className="text-[11px] font-display font-semibold uppercase tracking-[0.12em]"
                                                        style={{ color: "var(--kp-red)" }}
                                                    >
                                                        Save
                                                    </button>
                                                )}
                                                <button
                                                    data-testid={HOLIDAYS.deleteBtn(h.id)}
                                                    onClick={() => remove(h.id)}
                                                    title="Remove"
                                                    className="text-neutral-400 hover:text-[color:var(--kp-red)]"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {rows.length === 0 && (
                            <tr><td colSpan={4} className="px-5 py-10 text-center text-neutral-400 text-xs">No holidays yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
